<template>
  <v-container fluid class='ma-0 pa-0'>

    <!-- Dialog -->
    <v-dialog v-model="visible" :max-width="width" persistent>
      <v-card>
        <v-toolbar dense dark flat :color="toolbarColor">
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-account</v-icon>
            {{ formTitle }}
          </v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pb-0 px-2">
          <v-alert
            border="top"
            colored-border
            type="success"
            elevation="2"
            class="my-1 pb-2 mb-0"
            >
            <p class="caption" v-html="alertText"></p>
          </v-alert>

          <v-container grid-list-md>
            <form>

              <!-- Row 1 (Email) -->
              <v-row no-gutters>
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="editedUser.email"
                    :label="getExpression('Label', 'Email')"
                    v-validate="'required|email'"
                    :error-messages="errors.collect('email')"
                    data-vv-name="email"
                    @keydown.space.prevent
                    ref="email"
                    class="mb-2"
                  />
                </v-col>
              </v-row>

              <!-- Row 2 (Name) -->
              <v-row no-gutters>
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="editedUser.name"
                    :label="getExpression('Label', 'Name')"
                    v-validate="'required|max:50|alpha_spaces'"
                    :error-messages="errors.collect('name')"
                    data-vv-name="name"
                    class="mb-3"
                  />
                </v-col>
              </v-row>

              <!-- Row 3 (Display Name & Language) -->
              <v-row no-gutters>
                <v-col v-if="isGenie" cols="12" sm="7">
                  <v-text-field
                    v-model="editedUser.nickname"
                    :label="getExpression('Label', 'DisplayName')"
                    v-validate="'max:20'"
                    :error-messages="errors.collect('nickname')"
                    data-vv-name="nickname"
                    class="mr-2"
                  />
                </v-col>

                <v-col cols="12" :sm="isGenie ? '5' : '12'">
                  <v-select
                    v-model="editedUser.languageId"
                    :items="languagedLanguages"
                    item-value="id"
                    item-text="description"
                    :label="getExpression('Label', 'Language')"
                  />
                </v-col>
              </v-row>

              <!-- Row 4 (Role Dropdown) -->
              <template v-if="showRoleDropdown">
                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <v-select
                      v-model="editedUser.functionalRole"
                      :label="getExpression('Label', 'Role')"
                      :items="functionalRoles"
                      @change="selectRoleChange($event)"
                      v-validate="'required'"
                      :error-messages="errors.collect('role')"
                      data-vv-name="role"
                      :disabled="disableRoleDropdown"
                      hide-details="auto"
                      class="mb-5"
                      >
                      <template v-slot:prepend>
                        <v-tooltip v-model="roleTooltip" top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn small icon @click="roleTooltip = !roleTooltip">
                              <v-icon>info</v-icon>
                            </v-btn>
                          </template>
                          <v-card>
                            <v-card-text class="py-3" v-html="roleInfoText" />
                          </v-card>
                        </v-tooltip>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </template>

              <!-- Rows 5 (Residents) -->
              <template v-if="showResidentSelector">
                <v-row no-gutters>
                  <v-col cols="12" xs="12" sm="12" md="12">
                    <resident-selector
                      v-model="editedUser.residentIds"
                      :options="residents"
                      :label="getExpression('Label', 'ConnectedResidents')"
                      v-validate="'required'"
                      :error="errors.first('resident')"
                      data-vv-name="resident"
                      ref="residents"
                    />
                  </v-col>
                </v-row>
              </template>
            </form>
          </v-container>
        </v-card-text>

        <v-card-actions class="pt-0">
          <v-spacer />

          <v-btn @click="cancel" :loading="isSaving" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
          <v-btn :color="toolbarColorDark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>

        </v-card-actions>

      </v-card>
    </v-dialog>

    <!-- Invitation Email Snackbar-->
    <v-snackbar v-model="invitationSnackbar" :timeout="3000">
      An invitation email has been sent!
      <v-btn dark text @click="invitationSnackbar = false" :color="appColor_Medium">
        Close
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import appService from '@/app.service.js'
import { mapGetters } from 'vuex'
import AppUser from '@/plugins/user.js'
import { AppColor, UserRole, KioskTypeId, LanguageId, AddContactContext, FunctionalUserRole } from '@/config.js'
import ResidentSelector from '@/components/ResidentSelector'
import { EventBus } from '@/event-bus.js'

export default {
  name: 'AppUser',
  components: {
    ResidentSelector
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // colors
      appColor_Abby: AppColor.KioskTypeAbby,
      appColor_AbbyDark: AppColor.KioskTypeAbbyDark,
      appColor_Genie: AppColor.KioskTypeGenieLight,
      appColor_GenieDark: AppColor.KioskTypeGenie,
      appColor_Resident: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,
      appColor_Medium: AppColor.Medium,
      appColor_Selected: AppColor.Selected,

      // kiosk types
      kioskTypeId_Abby: KioskTypeId.Abby,
      kioskTypeId_Genie: KioskTypeId.Genie,

      // roles
      role_Guest: UserRole.Guest,
      role_Manager: UserRole.Manager,

      // contexts
      context_KioskDashboard: AddContactContext.KioskDashboard,
      context_KioskDashboardResidentPanel: AddContactContext.KioskDashboardResidentPanel,
      context_ResidentDashboard: AddContactContext.ResidentDashboard,

      fixedPassword: '@mbient1',
      invitationSnackbar: false,

      isSaving: false,
      visible: false,
      toolbarColorCustom: null,
      toolbarColorDarkCustom: null,
      functionalRoles: [],
      residents: [],
      width: 330,
      onSave: {},
      onCancel: {},

      context: null,
      
      giveManagerPrivileges: false,
      showRoleDropdown: false,
      disableRoleDropdown: false,
      showResidentSelector: false,
      roleTooltip: false,

      editedUser: {
        id: null,
        name: null,
        email: null,
        nickname: null,
        picture: null,
        residentIds: null,
        defaultResidentId: null,
        defaultResidentName: null,
        role: this.role_Guest,
        functionalRole: null,
        languageId: null
      },
      defaultUser: {
        id: null,
        name: null,
        email: null,
        nickname: null,
        picture: null,
        residentIds: null,
        defaultResidentId: null,
        defaultResidentName: null,
        role: this.role_Guest,
        functionalRole: null,
        languageId: null
      }
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'languages',
      'activeKiosk',
      'activeResident',
      'isAdmin',
      'isKioskManager',
      'isResidentManager',
      'isDomainManager',
      'locale']),
    formTitle () {
      if (this.isAddMode) {
        if (this.context === this.context_ResidentDashboard)
          return this.isGenie
            ? this.getExpression('FormTitle', 'AddContact')
            : this.getExpression('FormTitle', 'AddFamilyFacilitator')
        else if (this.context === this.context_KioskDashboard)
          return this.getExpression('FormTitle', 'AddKioskManager')
        else if (this.context === this.context_KioskDashboardResidentPanel)
          return this.isGenie
            ? this.getExpression('FormTitle', 'AddContact')
            : this.getExpression('FormTitle', 'AddFamilyFacilitator')
        return ''
      } else {
        if (this.context === this.context_ResidentDashboard)
          return this.getExpression('FormTitle', 'EditContact')
        else if (this.context === this.context_KioskDashboard)
          return this.getExpression('FormTitle', 'EditKioskManager')
        else if (this.context === this.context_KioskDashboardResidentPanel)
          return this.isGenie
            ? this.getExpression('FormTitle', 'EditFContact')
            : this.getExpression('FormTitle', 'EditFamilyFacilitator')
            
        return ''
      }
    },
    isAddMode () {
      return this.editedUser.id === null
    },
    languagedLanguages () {
      const self = this
      if (this.isNullOrUndefined(this.languages)) return []

      return this.languages.map(l => { return {
        id: l.id,
        description: self.getExpression('Language', l.description)
      }})
    },
    dictionary () { 
      return {
        attributes: {
          email: 'Email'
        },
        custom: {
          language: {
            required: () => this.getExpression('Validation', 'LanguageRequired')
          },
          contactPhone: {
            max: this.getExpression('Validation', 'ContactPhoneMaxChars').replace('{0}', '20')
          },
          email: {
            email: this.getExpression('Validation', 'InvalidEmail'),
            required: () => this.getExpression('Validation', 'EmailRequired')
          },
          name: {
            required: () => this.getExpression('Validation', 'NameRequired'),
            max: this.getExpression('Validation', 'NameMaxChars').replace('{0}', '50'),
            alpha_spaces: this.getExpression('Validation', 'NameAlphaSpaces')
          },
          nickname: {
            max: this.getExpression('Validation', 'DisplayNameMaxChars').replace('{0}', '20'),
          },
          role: {
            required: () => this.getExpression('Validation', 'RoleRequired')
          },
          resident: {
            required: () => this.getExpression('Validation', 'ResidentRequired')
          }
        }
      }
    },
    residentsRule () {
      return this.isResidentRequired ? 'required' : ''
    },
    isResidentRequired () {
      return (
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'VirtualVisitor') ||
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'FamilyFacilitator')
      )
    },
    toolbarColor () {
      if (this.toolbarColorCustom !== null) return this.toolbarColorCustom
      if (this.context === this.context_ResidentDashboard) return this.appColor_Resident
      
      return this.isGenie
        ? this.appColor_Genie
        : this.appColor_Abby
    },
    toolbarColorDark () {
      if (this.toolbarColorDarkCustom !== null) return this.toolbarColorDarkCustom
      if (this.context === this.context_ResidentDashboard) return this.appColor_ResidentDark
      
      return this.isGenie
        ? this.appColor_GenieDark
        : this.appColor_AbbyDark
    },
    kioskTypeId () {
      if (this.isNullOrUndefined(this.context)) return null
      if (this.context === this.context_ResidentDashboard 
        && this.isNullOrUndefined(this.activeResident)) return null
      if (this.context === this.context_KioskDashboard 
        && this.isNullOrUndefined(this.activeKiosk)) return null

      return this.context === this.context_ResidentDashboard
        ? this.activeResident.kiosk.typeId
        : this.activeKiosk.typeId
    },
    isGenie () {
      return this.kioskTypeId === this.kioskTypeId_Genie
    },
    isAbby () {
      return this.kioskTypeId === this.kioskTypeId_Abby
    },
    newUserDescription () {
      return this.isGenie
        ? this.isResidentManager
          ? this.getExpression('FunctionalRole', 'VirtualVisitor')
          : this.editedUser.role === this.role_Manager
            ? this.getExpression('FunctionalRole', 'FamilyFacilitator')
            : this.getExpression('FunctionalRole', 'VirtualVisitor')
        : this.getExpression('FunctionalRole', 'FamilyFacilitator')
    },
    alertText () {
      const self = this
      var text = ''
      
      if (self.context === self.context_ResidentDashboard) {
        text = self.isGenie
         ? self.getExpression('Alert', 'ConnectContactToResident')
                .replace('{0}', self.activeResident.firstName)
         : self.getExpression('Alert', 'ConnectFFToResident')
                .replace('{0}', self.activeResident.firstName).replace('{1}', self.newUserDescription)
      } else if (self.context === self.context_KioskDashboard) {
        text = self.editedUser.functionalRole === self.getExpression('FunctionalRole', 'KioskManager')
          ? self.getExpression('Alert', 'AddKioskManager')
              .replace('{0}', self.getExpression('FunctionalRole', 'KioskManager'))
          : self.getExpression('Alert', 'AddAndConnectContactToSelectedResidents')
              .replace('{0}', self.editedUser.functionalRole)
      } else if (self.context === self.context_KioskDashboardResidentPanel) {
        text = self.isGenie
          ? self.getExpression('Alert', 'AddAndConnectContactToSelectedResidents').replace('{0}', self.newUserDescription)
          : self.getExpression('Alert', 'AddAndConnectFamilyFacilitator').replace('{0}', self.newUserDescription)
      }

      return text
    },
    roleInfoText () {
      const textRM = `&bull; ${this.getExpression('Alert', 'RoleFFInfoText1')}</br>` +
                            `&bull; ${this.getExpression('Alert', 'RoleFFInfoText2')}</br>` +
                            `&bull; ${this.getExpression('Alert', 'RoleFFInfoText3')}</br>` +
                            `&bull; ${this.getExpression('Alert', 'RoleFFInfoText4')}`

      const textVV = `&bull; ${this.getExpression('Alert', 'RoleVVInfoText')}</br>`

      return this.editedUser.functionalRole === FunctionalUserRole.ResidentManager
        ? textRM
        : textVV
    }
  },
  methods: {
    add (params) {
      this.initParams(params)

      // --- RESIDENT DASHBOARD DEFAULTS
      if (this.context === this.context_ResidentDashboard) {
        // this.showRoleCheckbox = this.isGenie && !this.isResidentManager
        this.showRoleDropdown = false //this.isGenie && !this.isResidentManager

        // role
        this.editedUser.role = this.isAbby
          ? this.role_Manager
          : this.role_Guest

        // language
        this.editedUser.languageId = !this.isNullOrUndefined(this.activeResident)
          ? this.activeResident.languageId
          : LanguageId.English

      // --- KIOSK DASHBOARD DEFAULTS
      } else if (this.context === this.context_KioskDashboard 
        || this.context === this.context_KioskDashboardResidentPanel) {
        this.showRoleDropdown = this.functionalRoles?.length !== 1
        
        // language
        this.editedUser.languageId = !this.isNullOrUndefined(this.activeKiosk)
          ? this.activeKiosk.languageId
          : LanguageId.English

        // role
        this.editedUser.role = this.isDomainManager || this.isAdmin
          ? this.role_Manager
          : this.role_Guest

        // functional role
        if (this.functionalRoles?.length > 0) {
          this.editedUser.functionalRole = this.functionalRoles[0]
        } else this.editedUser.functionalRole = 'NO ROLES AVAILABLE'
      }

      // disable role dropdown if only 1 role
      this.disableRoleDropdown = this.functionalRoles?.length < 2

      // KMs can only add FFs and VVs so auto-show the resident picker
      this.showResidentSelector = 
        (this.context === this.context_KioskDashboardResidentPanel)
          //&& !this.isDomainManager

      this.visible = true
      this.setFocus()
    },

    edit (params) {
      this.initParams(params)
      this.visible = true
      this.setFocus()
    },

    initParams (params) {
      if (this.isNullOrUndefined(params)) return
      this.context = params.context
      if (!this.isNullOrUndefined(params.functionalRoles)) this.functionalRoles = params.functionalRoles
      if (!this.isNullOrUndefined(params.residents)) this.residents = params.residents
      if (!this.isNullOrUndefined(params.toolbarColor)) this.toolbarColorCustom = params.toolbarColor
      if (!this.isNullOrUndefined(params.toolbarColorDark)) this.toolbarColorDarkCustom = params.toolbarColorDark
      if (!this.isNullOrUndefined(params.width)) this.width = params.width
      if (!this.isNullOrUndefined(params.onSave)) this.onSave = params.onSave
      if (!this.isNullOrUndefined(params.onCancel)) this.onCancel = params.onCancel

      this.$validator.localize('en', this.dictionary)
      this.$validator.reset()
    },

    save () {
      const self = this
      self.$validator.resume()
      self.$validator.validateAll().then(valid => {
        if (valid) {
          self.isSaving = true
          const model = self.getModel(self.editedUser)
          const target = self.context === self.context_ResidentDashboard ||
            self.context === self.context_KioskDashboardResidentPanel
            ? 'resident'
            : 'kiosk'

          appService.createUserFromDashboard(model, target, self.locale).then(data => {
            self.isSaving = false
            if (data.errorSeverity !== null) {
              self.handleError(data)
            } else {
              self.showSummary(data.isEmailSent)
              self.reset()
              self.visible = false
              if (typeof this.onSave === 'function') {
                this.onSave(data)
              }
            }
          }).catch(e => { self.handleCriticalError(e) })
        } else {
          self.$validator.pause()
        }
      })
    },

    getModel (u) {
      const self = this

      u.picture = null
      u.password = self.fixedPassword

      const name = u.name !== null
        ? u.name.trim()
        : null

      const nicknameSafe = u.nickname !== null
        ? u.nickname.length > 0
           ? u.nickname.trim()
           : ''
        : ''

      // derive nickname from full name
      const nickname = (nicknameSafe.length === 0 && name !== null)
        ? name.trim().split(' ')[0].substring(0, 20)
        : nicknameSafe

      // language
      const languageId = u.languageId === LanguageId.English
        ? null
        : u.languageId

      var kioskIds = null
      var residentIds = null
      var role = self.role_Guest
      var defaultResidentId = null

      if (self.context === self.context_KioskDashboardResidentPanel) {

        // role
        role = self.getSelectedRole(u)

        //--- FF or VV
        if ((u.residentIds ?? []).length > 0) {

          residentIds = u.residentIds

          // default resident
          defaultResidentId = residentIds.length > 1
            ? u.defaultResidentId === null
              ? residentIds[0]
              : u.defaultResidentId
            : null
        }

      } else if (self.context === self.context_KioskDashboard)  {
          // KM
          role = u.role
          kioskIds = [self.activeKiosk.id]

      } else if (self.context === self.context_ResidentDashboard) {
        role = u.role
        residentIds = [self.activeResident.id]
      }

      // return the model
      return {
        id: u.id,
        email: u.email,
        name: name,
        password: u.password,
        nickname: nickname,
        languageId: languageId,
        role: role,
        residentIds: residentIds,
        defaultResidentId: defaultResidentId,
        kioskIds: kioskIds,
        updateByUserId: self.profile.id,
        addContactContext: self.context,
        createDate: u.createDate ?? new Date()
      }
    },

    getSelectedRole (user) {
      var role = this.role_Guest
      if ([this.getExpression('FunctionalRole', 'FamilyFacilitator'),
        this.getExpression('FunctionalRole', 'KioskManager')].includes(user.functionalRole)) {
        role = this.role_Manager
      } else if (
        user.functionalRole === this.getExpression('FunctionalRole', 'VirtualVisitor')
      ) {
        role = this.role_Guest
      }
      return role
    },

    cancel () {
      const self = this

      if (typeof self.onCancel === 'function') {
        self.onCancel()
        self.visible = false
      } else {
        self.visible = false
      }
      setTimeout(() => { self.reset() }, 300)
    },

    reset () {
      this.editedUser = Object.assign({}, this.defaultUser)
      this.$validator.reset()
      this.$validator.pause()
    },

    showSummary (isEmailSent) {
      const self = this
      const isGenie = !self.isAbby
      const isResidentDashboard = self.context === self.context_ResidentDashboard
      const isKioskDashboardResidentPanel = self.context === self.context_KioskDashboardResidentPanel
      const confirmation1 = `${self.getExpression('Alert', 'ContactAddConfirmation1')}`

      // the text describing how they need to install the mobile app (Genie users only)
      const confirmation2 = `${self.getExpression('Alert', 'ContactAddConfirmation2')
        .replace('{0}', self.newUserDescription)}`

      self.$alert.show({
        title: isResidentDashboard || isKioskDashboardResidentPanel
          ? self.getExpression('FormTitle', 'ContactConnected')
          : self.getExpression('FormTitle', 'ManagerCreated'),
        text: isEmailSent
          ? isResidentDashboard
            ? confirmation1 +
              isGenie
                ? `</br></br>${confirmation2}`
                : ''
            : confirmation1
          : isResidentDashboard || isKioskDashboardResidentPanel
            ? isResidentDashboard
              ? self.getExpression('Alert', 'UserDiscoveredAndConnected')
                .replace('{0}', self.editedUser.email)
                .replace('{1}', self.activeResident?.firstName ?? 'this resident')
              : self.getExpression('Alert', 'ContactAddConfirmation3').replace('{0}', self.editedUser.email)
            : self.getExpression('Alert', 'UserDiscoveredAndKioskAssigned')
              .replace('{0}', self.editedUser.email),
          formColor: 'success',
          icon: 'done'
      })
    },

    setFocus () {
      const self = this
      setTimeout(() => {
        if (!this.isNullOrUndefined(self.$refs.email)) {
          self.$refs.email.focus()
        }
      }, 500)
    },

    // role checkbox events
    checkRoleChange (e) {
      this.editedUser.role = e === true
        ? this.role_Manager
        : this.role_Guest

      this.roleChange()
    },

    // role select events
    selectRoleChange (e) {
      if (this.editedUser.functionalRole === null && this.isDomainManager) return false
      this.showResidentSelector = this.isDomainManager || this.isAdmin
        ? e !== this.getExpression('FunctionalRole', 'KioskManager')
        : true

      this.editedUser.role = (e === this.getExpression('FunctionalRole', 'VirtualVisitor'))
        ? this.role_Guest
        : this.role_Manager

      this.roleChange()
    },

    // mutations
    roleChange () {
      // add behavioiur only
      if (this.editedUser.id === null && this.editedUser.email === null) {
        this.setFocus()
      }
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  beforeMount() {
    AppUser.EventBus.$on('add', (params) => {
      this.add(params)
    })
    AppUser.EventBus.$on('edit', (params) => {
      this.edit(params)
    })
    EventBus.$on('mouse-click', () => {
      if (this.roleTooltip === true) {
        this.roleTooltip = false
      }
    })
  }
}
</script>